import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import * as z from 'zod';
import { IconArrowBackUp, IconCopy, IconTrash } from '@rossum/ui/icons/tabler';
import { SvgIcon, Tooltip } from '@rossum/ui/material';
import {
  GridActionsCellItem,
  GridColDef,
  GridColType,
} from '@rossum/ui/x-data-grid-pro';
import { dateFnsLocales } from '../../../i18n/dateLocales';
import { parseJsonPreprocessor } from '../../../lib/parseJsonPreprocessor';
import { dataTypes } from '../columns/dataTypes';
import {
  Dataset,
  ElisColumnType,
  getColumnsFromMetadata,
  isEditableColumn,
  isValidMongoKey,
} from '../types';
import { ColumnHeader } from './ColumnHeader';
import { DateEditCell } from './DateEditCell';
import {
  formatDateCell,
  utcInstantToWallClock,
  wallClockToUtcIso,
} from './dateUtils';
import { Row, ROW_STATUS } from './types';

export const serializeJsonCell = (value: unknown): string =>
  value === undefined || value === null ? '' : JSON.stringify(value);

type JsonCellType = 'object' | 'array' | 'unknown';

const jsonCellShapeByType: Record<JsonCellType, z.ZodType> = {
  object: z.record(z.string(), z.unknown()),
  array: z.array(z.unknown()),
  unknown: z.unknown(),
};

export const parseJsonCellText = (
  value: unknown,
  type: JsonCellType = 'unknown'
): unknown => {
  const text = typeof value === 'string' ? value.trim() : '';
  if (text === '') return null;

  const result = z
    .preprocess(parseJsonPreprocessor, jsonCellShapeByType[type])
    .safeParse(text);

  return result.success ? result.data : undefined;
};

const dateColumnProps = (
  field: string,
  locale: Locale
): Partial<GridColDef<Row>> => ({
  valueGetter: (value: unknown): Date | null => {
    if (typeof value !== 'string' || value.trim() === '') return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : utcInstantToWallClock(date);
  },
  valueFormatter: (value: Date | null): string =>
    value ? formatDateCell(value, locale) : '',
  valueSetter: (value, row): Row =>
    value instanceof Date && !Number.isNaN(value.getTime())
      ? { ...row, [field]: wallClockToUtcIso(value) }
      : value == null
        ? { ...row, [field]: null }
        : row,
  renderEditCell: params => <DateEditCell {...params} />,
});

const jsonColumnProps = (
  field: string,
  type: JsonCellType
): Partial<GridColDef<Row>> => ({
  valueGetter: (value: unknown): string => serializeJsonCell(value),
  valueSetter: (value, row): Row => {
    const parsed = parseJsonCellText(value, type);
    return parsed === undefined ? row : { ...row, [field]: parsed };
  },
  preProcessEditCellProps: params => ({
    ...params.props,
    error: parseJsonCellText(params.props.value, type) === undefined,
  }),
});

// The actions column needs a field name of its own, but dataset columns are
// user data and one of them may well be called `actions`. Two columns sharing a
// field silently collapse into one in DataGrid — the dataset's column would be
// the one lost — so step out of the way until the name is free.
const freeFieldName = (name: string, taken: ReadonlySet<string>): string =>
  taken.has(name) ? freeFieldName(`_${name}`, taken) : name;

type Props = {
  table: Dataset;
  editingDisabled: boolean;
  onDeleteRow: (row: Row) => void;
  onDuplicateRow: (row: Row) => void;
  onRestoreRow: (row: Row) => void;
};

const typeToGridType: Record<ElisColumnType, GridColType | undefined> = {
  bool: 'boolean',
  date: 'dateTime',
  object: 'string',
  array: 'string',
  number: 'number',
  string: 'string',
  unknown: undefined,
};

export const useColumns = ({
  table,
  editingDisabled,
  onDeleteRow,
  onDuplicateRow,
  onRestoreRow,
}: Props) => {
  const intl = useIntl();
  const locale = dateFnsLocales[intl.locale];

  return useMemo<{ columns: GridColDef<Row>[]; actionsField: string }>(() => {
    const typeLabels = dataTypes(intl);
    const dataColumns = getColumnsFromMetadata(
      table.metadata,
      table.schema
    ).map(
      column =>
        ({
          headerName: column.name,
          renderHeader: () => (
            <ColumnHeader
              name={column.name}
              typeLabel={typeLabels[column.type].label}
            />
          ),
          field: column.datasetAttributeName,
          flex: 1,
          sortable: isValidMongoKey(column.datasetAttributeName),
          filterable: false,
          minWidth: 120,
          editable: !editingDisabled && isEditableColumn(column),
          type: typeToGridType[column.type],
          ...(column.type === 'date'
            ? dateColumnProps(column.datasetAttributeName, locale)
            : column.type === 'object' ||
                column.type === 'array' ||
                column.type === 'unknown'
              ? jsonColumnProps(column.datasetAttributeName, column.type)
              : {}),
        }) satisfies GridColDef<Row>
    );

    const actionsField = freeFieldName(
      'actions',
      new Set(dataColumns.map(column => column.field))
    );

    const actionsColumn: GridColDef<Row> = {
      field: actionsField,
      type: 'actions',
      width: 90,
      getActions: ({ row }) => {
        if (row[ROW_STATUS] === 'new') {
          const removeLabel = intl.formatMessage({
            id: 'features.datasets.data.edit.deleteRow',
          });
          const duplicateLabel = intl.formatMessage({
            id: 'features.datasets.data.edit.duplicateRow',
          });
          return [
            <Tooltip
              key={`duplicate-${row._id}`}
              title={editingDisabled ? '' : duplicateLabel}
            >
              <span>
                <GridActionsCellItem
                  data-cy={`duplicate-row-${row._id}`}
                  disabled={editingDisabled}
                  icon={
                    <SvgIcon fontSize="large">
                      <IconCopy />
                    </SvgIcon>
                  }
                  onClick={() => onDuplicateRow(row)}
                  label={duplicateLabel}
                />
              </span>
            </Tooltip>,
            <Tooltip
              key={`remove-${row._id}`}
              title={editingDisabled ? '' : removeLabel}
            >
              <span>
                <GridActionsCellItem
                  data-cy={`remove-row-${row._id}`}
                  disabled={editingDisabled}
                  icon={
                    <SvgIcon fontSize="large">
                      <IconTrash />
                    </SvgIcon>
                  }
                  onClick={() => onRestoreRow(row)}
                  label={removeLabel}
                />
              </span>
            </Tooltip>,
          ];
        }

        if (row[ROW_STATUS] === 'deleted' || row[ROW_STATUS] === 'edited') {
          const label = intl.formatMessage({
            id:
              row[ROW_STATUS] === 'deleted'
                ? 'features.datasets.data.edit.restoreRow'
                : 'features.datasets.data.edit.undoRow',
          });
          return [
            <Tooltip
              key={`restore-${row._id}`}
              title={editingDisabled ? '' : label}
            >
              <span>
                <GridActionsCellItem
                  data-cy={`restore-row-${row._id}`}
                  disabled={editingDisabled}
                  icon={
                    <SvgIcon fontSize="large">
                      <IconArrowBackUp />
                    </SvgIcon>
                  }
                  onClick={() => onRestoreRow(row)}
                  label={label}
                />
              </span>
            </Tooltip>,
          ];
        }

        const deleteLabel = intl.formatMessage({
          id: 'features.datasets.data.edit.deleteRow',
        });
        const duplicateLabel = intl.formatMessage({
          id: 'features.datasets.data.edit.duplicateRow',
        });
        return [
          <Tooltip
            key={`duplicate-${row._id}`}
            title={editingDisabled ? '' : duplicateLabel}
          >
            <span>
              <GridActionsCellItem
                data-cy={`duplicate-row-${row._id}`}
                disabled={editingDisabled}
                icon={
                  <SvgIcon fontSize="large">
                    <IconCopy />
                  </SvgIcon>
                }
                onClick={() => onDuplicateRow(row)}
                label={duplicateLabel}
              />
            </span>
          </Tooltip>,
          <Tooltip
            key={`delete-${row._id}`}
            title={editingDisabled ? '' : deleteLabel}
          >
            <span>
              <GridActionsCellItem
                data-cy={`delete-row-${row._id}`}
                disabled={editingDisabled}
                icon={
                  <SvgIcon fontSize="large">
                    <IconTrash />
                  </SvgIcon>
                }
                onClick={() => onDeleteRow(row)}
                label={deleteLabel}
              />
            </span>
          </Tooltip>,
        ];
      },
    };

    return { columns: [...dataColumns, actionsColumn], actionsField };
  }, [
    table.metadata,
    table.schema,
    editingDisabled,
    onDeleteRow,
    onDuplicateRow,
    onRestoreRow,
    intl,
    locale,
  ]);
};
